import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { HashtagIcon } from '@heroicons/react/24/outline';
import { getTrendingHashtags } from '../../services/hashtagService';
import { useTranslation } from 'react-i18next';

interface TrendingHashtag {
  id: string;
  tag: string;
  count: number;
  score: number;
  postCount: number;
}

const TrendingHashtagsSidebar: React.FC = () => {
  const [hashtags, setHashtags] = useState<TrendingHashtag[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { t } = useTranslation();

  useEffect(() => {
    const fetchTrendingHashtags = async () => {
      try {
        setLoading(true);
        const trendingHashtags = await getTrendingHashtags(5);
        setHashtags(trendingHashtags);
      } catch (error) {
        console.error('Error fetching trending hashtags:', error);
        setError('No se pudieron cargar las tendencias');
      } finally {
        setLoading(false);
      }
    };

    fetchTrendingHashtags();

    // Actualizar cada 5 minutos
    const intervalId = setInterval(fetchTrendingHashtags, 5 * 60 * 1000);
    return () => clearInterval(intervalId);
  }, []);

  if (loading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4">
        <div className="flex items-center mb-4">
          <HashtagIcon className="h-5 w-5 mr-2 text-domired-600 dark:text-domired-400" />
          <h2 className="text-lg font-bold text-gray-900 dark:text-white">{t('home.trendingHashtags')}</h2>
        </div>
        <div className="animate-pulse space-y-3">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-3/4"></div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4">
        <div className="flex items-center mb-4">
          <HashtagIcon className="h-5 w-5 mr-2 text-domired-600 dark:text-domired-400" />
          <h2 className="text-lg font-bold text-gray-900 dark:text-white">{t('home.trendingHashtags')}</h2>
        </div>
        <p className="text-red-500 dark:text-red-400 text-sm">{error}</p>
      </div>
    );
  }

  if (hashtags.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4">
        <div className="flex items-center mb-4">
          <HashtagIcon className="h-5 w-5 mr-2 text-domired-600 dark:text-domired-400" />
          <h2 className="text-lg font-bold text-gray-900 dark:text-white">{t('home.trendingHashtags')}</h2>
        </div>
        <p className="text-gray-500 dark:text-gray-400 text-sm">No hay tendencias disponibles en este momento.</p>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4">
      <div className="flex items-center mb-4">
        <HashtagIcon className="h-5 w-5 mr-2 text-domired-600 dark:text-domired-400" />
        <h2 className="text-lg font-bold text-gray-900 dark:text-white">{t('home.trendingHashtags')}</h2>
      </div>
      <div className="space-y-4">
        {hashtags.map((hashtag) => (
          <Link
            key={hashtag.id}
            to={`/hashtag/${hashtag.id}`}
            className="block group hover:bg-gray-50 dark:hover:bg-gray-700 -mx-4 px-4 py-2 rounded-md transition-colors"
          >
            <div className="flex flex-col">
              <span className="text-domired-600 dark:text-domired-400 font-medium group-hover:underline">
                {hashtag.tag}
              </span>
              <span className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {hashtag.count} {hashtag.count === 1 ? 'publicación' : 'publicaciones'}
              </span>
            </div>
          </Link>
        ))}
      </div>
      <Link
        to="/explore/hashtags"
        className="block text-sm text-domired-600 dark:text-domired-400 hover:underline mt-4 text-center"
      >
        Ver más tendencias
      </Link>
    </div>
  );
};

export default TrendingHashtagsSidebar;
